import JSZip from 'jszip';
import { POI_TYPES } from '../../constants/poiTypes';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { logError, logInfo } from '../../utils/logger';

export const createKMLExport = async (survey) => {
  try {
    // Fetch POIs and routes
    const [pois, routes] = await Promise.all([
      fetchPOIs(survey.id),
      fetchRoutes(survey.id)
    ]);

    let kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${survey.name}</name>
    <description>${survey.description || ''}</description>`;

    // Add styles
    Object.entries(POI_TYPES).forEach(([type, config]) => {
      kml += `
    <Style id="${type}Style">
      <IconStyle>
        <color>${config.color.replace('#', 'ff')}</color>
        <scale>1.0</scale>
      </IconStyle>
    </Style>`;
    });

    // Add POIs
    pois.forEach(poi => {
      kml += `
    <Placemark>
      <name>${POI_TYPES[poi.type]?.prefix}-${poi.id}</name>
      <description>${poi.notes || ''}</description>
      <styleUrl>#${poi.type}Style</styleUrl>
      <Point>
        <coordinates>${poi.coordinates.longitude},${poi.coordinates.latitude}</coordinates>
      </Point>
    </Placemark>`;
    });

    // Add routes
    routes.forEach(route => {
      kml += `
    <Placemark>
      <name>${route.name}</name>
      <LineString>
        <coordinates>
          ${route.trackPoints.map(p => `${p.longitude},${p.latitude}`).join('\n          ')}
        </coordinates>
      </LineString>
    </Placemark>`;
    });

    kml += `
  </Document>
</kml>`;

    return kml;
  } catch (error) {
    throw new Error(`Failed to create KML: ${error.message}`);
  }
};

export const createKMZExport = async (survey) => {
  const zip = new JSZip();
  
  // Add main KML file
  const kml = await createKMLExport(survey);
  zip.file('doc.kml', kml);

  // Add POI photos if available
  const pois = await fetchPOIs(survey.id);
  const photoPromises = pois.flatMap(poi => 
    (poi.photos || []).map(async photoUrl => {
      if (!photoUrl || typeof photoUrl !== 'string') {
        logInfo(`Skipping invalid photo URL for POI ${poi.id}`);
        return null;
      }

      try {
        const response = await fetch(photoUrl, {
          mode: 'cors',
          credentials: 'same-origin'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const filename = `images/${poi.id}_${Date.now()}.jpg`;
        zip.file(filename, blob);
        return filename;
      } catch (error) {
        logError(`Failed to add photo for POI ${poi.id}:`, error);
        return null;
      }
    })
  );

  // Wait for all photo promises to resolve, filtering out failed ones
  const photoResults = await Promise.all(photoPromises);
  const successfulPhotos = photoResults.filter(Boolean);
  
  logInfo(`Successfully added ${successfulPhotos.length} photos to KMZ export`);
  
  return zip.generateAsync({ type: 'blob' });
};

async function fetchPOIs(surveyId) {
  const poisRef = collection(db, 'pois');
  const poisQuery = query(poisRef, where('surveyId', '==', surveyId));
  const snapshot = await getDocs(poisQuery);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

async function fetchRoutes(surveyId) {
  const routesRef = collection(db, 'routes');
  const routesQuery = query(routesRef, where('surveyId', '==', surveyId));
  const snapshot = await getDocs(routesQuery);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}